import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../components/ui/Table";
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import { Edit, Trash2, Plus } from "lucide-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { CardHeader, CardTitle } from "../../../components/ui/Card";
import PermissionGuard from "../../../components/auth/PermissionGuard";

const BASE_URL = "https://tyka.premierhostings.com/beta/api";

const ParentCategories = () => {
  const navigate = useNavigate();

  const [parentCategories, setParentCategories] = useState([]);
  const [selectedParents, setSelectedParents] = useState([]);
  const [loading, setLoading] = useState(false);

  // URL is the source of truth: ?page=2&per_page=20&search=cricket
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(
    1,
    parseInt(searchParams.get("page") || "1", 10) || 1,
  );
  const itemsPerPage = parseInt(searchParams.get("per_page") || "10", 10) || 10;
  const debouncedSearch = searchParams.get("search") || "";

  // Update URL params (defaults are removed to keep the URL clean)
  const updateParams = (updates) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          const isDefault =
            (key === "page" && Number(value) === 1) ||
            (key === "per_page" && Number(value) === 10);
          if (value === "" || value == null || isDefault) next.delete(key);
          else next.set(key, String(value));
        });
        return next;
      },
      { replace: true },
    );
  };
  const setCurrentPage = (page) => updateParams({ page });

  const [totalItems, setTotalItems] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  // Input text is local; it is written to the URL after a debounce
  const [searchTerm, setSearchTerm] = useState(debouncedSearch);

  // Bumping this forces a refetch (used after deletes)
  const [refreshKey, setRefreshKey] = useState(0);
  const refetch = () => setRefreshKey((k) => k + 1);

  const token =
    localStorage.getItem("token") || localStorage.getItem("authToken");

  // Debounce search input -> URL
  useEffect(() => {
    const t = setTimeout(() => {
      const value = searchTerm.trim();
      if (value !== debouncedSearch) updateParams({ search: value, page: 1 });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleUnauthorized = useCallback(() => {
    toast.error("Session expired. Please log in again.");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    navigate("/login");
  }, [navigate]);

  // Fetch the current page from the server
  useEffect(() => {
    if (!token) return;

    const controller = new AbortController();

    const params = new URLSearchParams({
      page: String(currentPage),
      per_page: String(itemsPerPage),
    });
    if (debouncedSearch) params.set("search", debouncedSearch);

    setLoading(true);

    fetch(`${BASE_URL}/product-sub-categories?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      signal: controller.signal,
    })
      .then(async (res) => {
        if (res.status === 401) {
          handleUnauthorized();
          return null;
        }
        if (!res.ok) throw new Error("Failed to load categories");
        return res.json();
      })
      .then((json) => {
        if (!json) return;

        const total = json.meta?.total ?? json.data?.length ?? 0;
        const last = json.meta?.last_page ?? 1;

        // If we're past the last page (e.g. after deleting the last row
        // on a page), step back one page.
        if (currentPage > last && last >= 1) {
          setCurrentPage(last);
          return;
        }

        setParentCategories(json.data || []);
        setTotalItems(total);
        setLastPage(last);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error("Error fetching parent categories:", err);
        toast.error(err.message || "Error loading parent categories.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [
    token,
    currentPage,
    itemsPerPage,
    debouncedSearch,
    refreshKey,
    handleUnauthorized,
  ]);

  // Derived pagination values
  const totalPages = lastPage;
  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const showingFrom = parentCategories.length === 0 ? 0 : indexOfFirst + 1;
  const showingTo = indexOfFirst + parentCategories.length;

  // Selection helpers
  const areAllOnPageSelected =
    parentCategories.length > 0 &&
    parentCategories.every((cat) => selectedParents.includes(cat.id));

  const handleSelectAllCategories = () => {
    if (areAllOnPageSelected) {
      setSelectedParents((prev) =>
        prev.filter((id) => !parentCategories.some((cat) => cat.id === id)),
      );
    } else {
      const newIds = parentCategories
        .map((cat) => cat.id)
        .filter((id) => !selectedParents.includes(id));
      setSelectedParents((prev) => [...prev, ...newIds]);
    }
  };

  const handleSelectCategory = (id) => {
    setSelectedParents((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id],
    );
  };

  // Single delete
  const deleteCategory = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action will permanently delete the category.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (!result.isConfirmed) return;

      fetch(`${BASE_URL}/product-sub-categories/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
        .then(async (res) => {
          if (res.status === 401) {
            handleUnauthorized();
            return;
          }
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data?.message || "Failed to delete");
          }

          setSelectedParents((prev) => prev.filter((cid) => cid !== id));
          Swal.fire(
            "Deleted!",
            "The Parent Category has been deleted.",
            "success",
          );
          refetch();
        })
        .catch((err) => {
          console.error("Delete error:", err);
          toast.error(err.message || "Failed to delete the Parent Category.");
        });
    });
  };

  // Bulk delete: ONE request with all ids
  const handleBulkDelete = () => {
    if (!token) {
      toast.error("No token found");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "You are about to delete selected categories. This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete them!",
    }).then((result) => {
      if (!result.isConfirmed) return;

      fetch(`${BASE_URL}/product-sub-categories/bulk-delete`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ ids: selectedParents }),
      })
        .then(async (res) => {
          if (res.status === 401) {
            handleUnauthorized();
            return;
          }
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data?.message || "Bulk delete failed");
          }

          Swal.fire("Deleted!", "Selected categories were deleted.", "success");
          setSelectedParents([]);
          refetch();
        })
        .catch((err) => {
          console.error("Batch delete error:", err);
          toast.error(err.message || "Error deleting selected categories.");
        });
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Card Header */}
      <CardHeader className="space-y-4">
        <div className="flex flex-col space-y-2">
          <CardTitle className="text-lg sm:text-xl">
            Parent Categories
          </CardTitle>
        </div>
        <div className="flex flex-col sm:flex-row sm:justify-end gap-3 w-full">
          {selectedParents.length > 0 && (
            <PermissionGuard permission="bulk_delete_parent_categories">
              <Button
                className="flex-shrink-0 w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white"
                onClick={handleBulkDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Bulk Delete ({selectedParents.length})
              </Button>
            </PermissionGuard>
          )}
          <PermissionGuard permission="create_parent_categories">
            <Button
              className="flex-shrink-0 w-full sm:w-auto"
              onClick={() => navigate("/add-parentcategory")}
            >
              <Plus className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Add Parent Category</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </PermissionGuard>
        </div>
      </CardHeader>

      {/* Top Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-4">
        <div>
          Show{" "}
          <select
            value={itemsPerPage}
            onChange={(e) =>
              updateParams({ per_page: parseInt(e.target.value, 10), page: 1 })
            }
            className="border p-1 rounded bg-transparent"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>{" "}
          entries
        </div>
        <div className="flex items-center rounded-md px-3 py-2 flex-1 max-w-xs sm:max-w-sm">
          <Input
            placeholder="Search parent categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border p-1 rounded bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 w-full min-w-0"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto w-full">
        <Table className="min-w-[800px] w-full" id="parent-categories-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                  checked={areAllOnPageSelected}
                  onChange={handleSelectAllCategories}
                />
              </TableHead>
              <TableHead className="min-w-[150px] w-[20%]">
                Head Category
              </TableHead>
              <TableHead className="min-w-[200px] w-[20%]">
                Parent Category Name
              </TableHead>
              <TableHead className="min-w-[150px] w-[20%]">Slug</TableHead>
              <TableHead className="min-w-[100px] w-[10%] text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {loading && parentCategories.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-4 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : parentCategories.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-4 text-muted-foreground"
                >
                  No parent categories found.
                </TableCell>
              </TableRow>
            ) : (
              parentCategories.map((cat) => (
                <TableRow
                  key={cat.id}
                  className={loading ? "opacity-60" : undefined}
                >
                  <TableCell>
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                      checked={selectedParents.includes(cat.id)}
                      onChange={() => handleSelectCategory(cat.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    {cat.parent_category?.name || "N/A"}
                  </TableCell>
                  <TableCell className="font-medium">{cat.name}</TableCell>
                  <TableCell className="font-medium">{cat.slug}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <PermissionGuard permission="edit_parent_categories">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            navigate(`/edit-parentcategory/${cat.id}`)
                          }
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </PermissionGuard>
                      <PermissionGuard permission="delete_parent_categories">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteCategory(cat.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </PermissionGuard>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Bottom Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-4 text-sm">
        <div>
          Showing {showingFrom} to {showingTo} of {totalItems} entries
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            size="sm"
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </Button>

          {/* Pagination with ellipsis */}
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (pageNum) =>
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1),
            )
            .map((pageNum, idx, arr) => {
              const prev = arr[idx - 1];
              return (
                <React.Fragment key={pageNum}>
                  {prev && pageNum - prev > 1 && (
                    <span className="px-2">...</span>
                  )}
                  <Button
                    onClick={() => setCurrentPage(pageNum)}
                    size="sm"
                    className={`px-3 py-1 border rounded ${
                      currentPage === pageNum
                        ? "!bg-red-600 !text-white"
                        : "!bg-white !text-black"
                    }`}
                  >
                    {pageNum}
                  </Button>
                </React.Fragment>
              );
            })}

          <Button
            disabled={currentPage >= totalPages}
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            size="sm"
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ParentCategories;
